import {
  BookedHockeyLessonsPaginatedResponse,
  BookedSkatingLessonsPaginatedResponse,
  BookedSkatingPass,
  BookedSkatingPassesPaginatedResponse,
  ContactUsPaginatedResponse,
  Coupon,
  CreateCouponRequest,
  CustomerWaiversPaginatedResponse,
  EmailTemplate,
  HockeyLesson,
  PaginatedResponse,
  PartyExtra,
  PartyNote,
  PartySession,
  PriceList,
  Season,
  SeasonListItem,
  SeasonStatistics,
  SessionTimes,
  SkatingLesson,
  SkatingPass,
  SkatingSession,
  BookedSkatingSessionHistoryResponse,
  BookingWaiverRemovalEntry,
  RequestedByType,
  WaiverBookingType,
  TicketAuditLogResponse,
  OrderSourceSummary,
  TimeSlot,
  UpdateUserAccount,
  UserAccountPaginatedResponse,
  UserAccountProfileResponse,
  Waiver,
} from '../@types';
import { BookedPartyPaginatedResponse } from '../@types/BookedParty';

import { RequestApi } from '../helpers';

const createSeasonDuration = async (seasonDuration: {
  startDate: string;
  endDate: string;
}) => {
  const response = await RequestApi.post('/admin/season', seasonDuration);
  return response;
};

const createSessionTimes = async (sessionTimes: SessionTimes[]) => {
  const response = await RequestApi.post('/admin/session-times', sessionTimes);
  return response;
};

const getCurrentSessionTimes = () => {
  return RequestApi.get<SessionTimes>(`/admin/session-times/current`);
};

const getCurrentSeasonDetails = async () => {
  return await RequestApi.get<Season>(`/admin/season/current`);
};

const getSeasons = async () => {
  return await RequestApi.get<SeasonListItem[]>(`/admin/seasons`);
};

const toggleTicketPurchases = async () => {
  const response = await RequestApi.post(`/admin/toggle-ticket-purchases`);
  return response;
};

const toggleSeasonPassPurchases = async () => {
  const response = await RequestApi.post(`/admin/toggle-season-pass-purchases`);
  return response;
};

const togglePartyPackagePurchases = async () => {
  const response = await RequestApi.post(
    `/admin/toggle-party-package-purchases`,
  );
  return response;
};

const toggleSkatingLessonPurchases = async () => {
  const response = await RequestApi.post(
    `/admin/toggle-skating-lesson-purchases`,
  );
  return response;
};

const toggleHockeyLessonPurchases = async () => {
  const response = await RequestApi.post(
    `/admin/toggle-hockey-lesson-purchases`,
  );
  return response;
};

const getTimeSlotsForDate = async (date: string) => {
  const response = await RequestApi.get<TimeSlot[]>(`/admin/sessions/${date}`);
  return response;
};

const updateTimeSlot = async (timeSlot: TimeSlot) => {
  const response = await RequestApi.patch(
    `/admin/sessions/${timeSlot.id}`,
    timeSlot,
  );
  return response;
};

// price list

const getCurrentPriceList = async () => {
  const response = await RequestApi.get<PriceList[]>(
    `/admin/season/current-price-list`,
  );
  return response;
};

const addPriceList = async (priceList: PriceList) => {
  const response = await RequestApi.post(`/admin/season/price-list`, priceList);
  return response;
};

// waiver

const getCurrentWaiver = async () => {
  const response = await RequestApi.get<Waiver>(`/admin/season/waiver`);
  return response;
};

const updateWaiver = async (waiver: Waiver) => {
  const response = await RequestApi.put(`/admin/season/waiver`, waiver);
  return response;
};

// email templates

const getEmailTemplates = async () => {
  const response = await RequestApi.get<EmailTemplate[]>(
    `/admin/season/email-templates`,
  );
  return response;
};

const updateEmailTemplate = async (
  emailId: string,
  emailTemplate: EmailTemplate,
) => {
  const response = await RequestApi.put(
    `/admin/season/email-templates/${emailId}`,
    emailTemplate,
  );
  return response;
};

const sendTestEmail = async (
  emailId: string,
  variables?: Record<string, string>,
) => {
  const response = await RequestApi.post(
    `/admin/season/email-templates/send-test`,
    {
      emailId,
      variables,
    },
  );
  return response;
};

// Hockey Lessons

const getHockeyLessons = async () => {
  const response = await RequestApi.get<HockeyLesson[]>(
    `/admin/season/hockey-lessons`,
  );
  return response;
};

const updateHockeyLesson = async (hockeyLesson: HockeyLesson) => {
  const response = await RequestApi.put(
    `/admin/season/hockey-lessons/${hockeyLesson.id}`,
    hockeyLesson,
  );
  return response;
};

// Skating Lessons

const getSkatingLessons = async () => {
  const response = await RequestApi.get<SkatingLesson[]>(
    `/admin/season/skating-lessons`,
  );
  return response;
};

const updateSkatingLesson = async (skatingLesson: SkatingLesson) => {
  const response = await RequestApi.put(
    `/admin/season/skating-lessons/${skatingLesson.id}`,
    skatingLesson,
  );
  return response;
};

// Contact Us Messages
const getContactUsMessages = async (
  pageNumber: number,
  pageSize: number,
  searchTerm: string,
) => {
  const response = await RequestApi.get<ContactUsPaginatedResponse>(
    `/admin/contact-us/messages?pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${searchTerm}`,
  );
  return response;
};

const markContactMessageAsRead = async (messageId: string) => {
  const response = await RequestApi.patch(
    `/admin/contact-us/messages/${messageId}/mark-read`,
  );
  return response;
};

const replyToContactMessage = async (messageId: string, reply: string) => {
  const response = await RequestApi.post(
    `/admin/contact-us/messages/${messageId}/reply`,
    { replyMessage: reply },
  );
  return response;
};

// Booked Sessions Endpoints
const getBookedSessions = async (
  pageNumber: number,
  pageSize: number,
  searchTerm: string,
  sessionDate?: string | null,
  sessionId?: string | null,
) => {
  let url = `/admin/booked/skating-sessions?pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${searchTerm}`;
  if (sessionDate) {
    url += `&sessionDate=${sessionDate}`;
  }
  if (sessionId) {
    url += `&sessionId=${sessionId}`;
  }
  const response = await RequestApi.get<PaginatedResponse<SkatingSession>>(url);
  return response;
};

// allowDateMismatch: staff confirmed redeeming a booking that is not for today
const redeemBookedSkatingSession = async (
  sessionId: number,
  options?: { allowDateMismatch?: boolean },
) => {
  const response = await RequestApi.patch(
    `/admin/booked/skating-sessions/${sessionId}/redeem`,
    {
      allowDateMismatch: options?.allowDateMismatch ?? false,
    },
  );
  return response;
};

const getBookedSkatingLessons = async (
  pageNumber: number,
  pageSize: number,
  searchTerm: string,
) => {
  const response = await RequestApi.get<BookedSkatingLessonsPaginatedResponse>(
    `/admin/booked/skating-lessons?pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${searchTerm}`,
  );
  return response;
};

const redeemBookedSkatingLesson = async (participantId: number) => {
  const response = await RequestApi.patch(
    `/admin/booked/skating-lessons/${participantId}/redeem`,
  );
  return response;
};

const getBookedHockeyLessons = async (
  pageNumber: number,
  pageSize: number,
  searchTerm: string,
) => {
  const response = await RequestApi.get<BookedHockeyLessonsPaginatedResponse>(
    `/admin/booked/hockey-lessons?pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${searchTerm}`,
  );
  return response;
};

const redeemBookedHockeyLesson = async (participantId: number) => {
  const response = await RequestApi.patch(
    `/admin/booked/hockey-lessons/${participantId}/redeem`,
  );
  return response;
};

const getBookedSkatingPasses = async (
  pageNumber: number,
  pageSize: number,
  searchTerm: string,
) => {
  const response = await RequestApi.get<BookedSkatingPassesPaginatedResponse>(
    `/admin/booked/skating-passes?pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${searchTerm}`,
  );
  return response;
};

const redeemBookedSkatingPass = async (
  passId: number,
  timeSlotId: string | number,
  options?: { overrideDailyLimit?: boolean; overrideReason?: string },
) => {
  const response = await RequestApi.patch(
    `/admin/booked/skating-passes/${passId}/redeem/${timeSlotId}`,
    {
      overrideDailyLimit: options?.overrideDailyLimit ?? false,
      overrideReason: options?.overrideReason ?? null,
    },
  );
  return response;
};

const getSeasonSkatingPasses = async () => {
  const response = await RequestApi.get<BookedSkatingPass[]>(
    `/admin/season/skating-passes`,
  );
  return response;
};

const updateSeasonSkatingPass = async (seasonPass: SkatingPass) => {
  const response = await RequestApi.put(
    `/admin/season/skating-pass/${seasonPass.id}`,
    seasonPass,
  );
  return response;
};

const getBookedParties = async (
  pageNumber: number,
  pageSize: number,
  searchTerm: string,
  sessionDate?: string | null,
) => {
  let url = `/admin/booked/parties?pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${searchTerm}`;
  if (sessionDate) {
    url += `&sessionDate=${sessionDate}`;
  }
  const response = await RequestApi.get<BookedPartyPaginatedResponse>(url);
  return response;
};

// allowDateMismatch: staff confirmed redeeming a booking that is not for today
const redeemBookedParty = async (
  partyId: number,
  options?: { allowDateMismatch?: boolean },
) => {
  const response = await RequestApi.patch(
    `/admin/booked/parties/${partyId}/redeem`,
    {
      allowDateMismatch: options?.allowDateMismatch ?? false,
    },
  );
  return response;
};

const updateBookedParty = async (partyId: number, updateData: any) => {
  const response = await RequestApi.put(
    `/admin/booked/parties/${partyId}`,
    updateData,
  );
  return response;
};

const getPartySessions = async () => {
  const response = await RequestApi.get<PartySession[]>(
    `/admin/season/parties`,
  );
  return response;
};

const updatePartySession = async (partySession: PartySession) => {
  const response = await RequestApi.put(
    `/admin/season/party/${partySession.id}`,
    partySession,
  );
  return response;
};

const getPartyExtras = async () => {
  const response = await RequestApi.get<PartyExtra[]>(
    `/admin/season/party-extras`,
  );
  return response;
};

const updatePartyExtra = async (partyExtra: PartyExtra) => {
  const response = await RequestApi.put(
    `/admin/season/party-extra/${partyExtra.id}`,
    partyExtra,
  );
  return response;
};

const addBookedPartyNote = async (partyId: number, note: string) => {
  const response = await RequestApi.post(
    `/admin/booked/party-notes/${partyId}/add`,
    {
      note,
    },
  );
  return response;
};

const getPartyNotes = async (partyId: number) => {
  const response = await RequestApi.get<PartyNote[]>(
    `/admin/booked/party-notes/${partyId}`,
  );
  return response;
};

const deleteBookedParty = async (partyId: number) => {
  const response = await RequestApi.delete(`/admin/booked/parties/${partyId}`);
  return response;
};

// Customer Waivers Endpoints
const getCustomerWaivers = async (
  pageNumber: number,
  pageSize: number,
  searchTerm: string,
) => {
  const response = await RequestApi.get<CustomerWaiversPaginatedResponse>(
    `/admin/customer-waivers?pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${searchTerm}`,
  );
  return response;
};

const getArchivedWaivers = async (filters: {
  seasonId: number;
  pageNumber: number;
  pageSize: number;
  searchTerm?: string;
}) => {
  const params = new URLSearchParams();
  params.set('seasonId', String(filters.seasonId));
  params.set('pageNumber', String(filters.pageNumber));
  params.set('pageSize', String(filters.pageSize));
  if (filters.searchTerm) params.set('searchTerm', filters.searchTerm);

  const response = await RequestApi.get<CustomerWaiversPaginatedResponse>(
    `/admin/archived-waivers?${params.toString()}`,
  );
  return response;
};

const searchCustomerWaivers = async (searchTerm: string) => {
  const response = await RequestApi.get<CustomerWaiversPaginatedResponse>(
    `/admin/customer-waivers/search?searchTerm=${searchTerm}`,
  );
  return response;
};

const addTicketsToSession = async (data: {
  timeSlotId: string;
  totalTickets: number;
  notes?: string;
}) => {
  const response = await RequestApi.post('/admin/sessions/add-tickets', data);
  return response;
};

const removeTicketsFromSession = async (data: {
  timeSlotId: string;
  totalTickets: number;
  notes?: string;
}) => {
  const response = await RequestApi.post(
    '/admin/sessions/remove-tickets',
    data,
  );
  return response;
};

const attachCustomerWaiverToRecord = async (
  recordType: string,
  recordId: number,
  waiverId: number,
  waiverUrl: string,
) => {
  const response = await RequestApi.patch(
    `/admin/booked/attach-customer-waiver`,
    { recordType, recordId, waiverId, waiverUrl },
  );
  return response;
};

const recordPaperWaiver = async (recordId: number) => {
  const response = await RequestApi.patch(`/admin/booked/record-paper-waiver`, {
    recordId,
  });
  return response;
};

const getAdminStatistics = async () => {
  const response = await RequestApi.get<SeasonStatistics>(
    `/admin/season/statistics`,
  );
  return response;
};

const createCoupon = async (coupon: CreateCouponRequest) => {
  const response = await RequestApi.post('/admin/coupons/createCoupon', coupon);
  return response;
};

const getAllCoupons = async () => {
  const response = await RequestApi.get<Coupon[]>(
    `/admin/coupons/getAllCoupons`,
  );
  return response;
};

const deleteCoupon = async (couponId: number) => {
  const response = await RequestApi.delete(`/admin/coupons/delete/${couponId}`);
  return response;
};

const cancelSkatingSession = async (
  sessionId: number,
  requestedByType: RequestedByType,
  reason?: string,
) => {
  const response = await RequestApi.delete(
    `/admin/booked/skating-sessions/${sessionId}`,
    { data: { requestedByType, reason } },
  );
  return response;
};

const permanentlyDeleteSkatingSession = async (
  sessionId: number,
  requestedByType: RequestedByType,
  reason?: string,
) => {
  const response = await RequestApi.delete(
    `/admin/booked/skating-sessions/${sessionId}/permanent`,
    { data: { requestedByType, reason } },
  );
  return response;
};

const changeSkatingSession = async (data: {
  id: number;
  oldSessionId: number;
  newSessionId: number;
  requestedByType: RequestedByType;
  notes?: string;
}) => {
  const response = await RequestApi.post(
    `/admin/booked/skating-sessions/change`,
    data,
  );
  return response;
};

const renameSkatingSessionHolder = async (
  recordId: number,
  newCustomerName: string,
  requestedByType: RequestedByType,
  notes?: string,
) => {
  const response = await RequestApi.patch(
    `/admin/booked/skating-sessions/${recordId}/rename`,
    { newCustomerName, requestedByType, notes },
  );
  return response;
};

const getBookedSkatingSessionHistory = async (recordId: number) => {
  const response = await RequestApi.get<BookedSkatingSessionHistoryResponse>(
    `/admin/booked/skating-sessions/${recordId}/history`,
  );
  return response;
};

const removeAttachedWaiver = async (
  recordType: WaiverBookingType,
  recordId: number,
  requestedByType: RequestedByType,
  notes?: string,
) => {
  const response = await RequestApi.patch(`/admin/booked/remove-waiver`, {
    recordType,
    recordId,
    requestedByType,
    notes,
  });
  return response;
};

const getWaiverRemovals = async (
  bookingType: Exclude<WaiverBookingType, 'skating_session'>,
  recordId: number,
) => {
  const params = new URLSearchParams({
    bookingType,
    recordId: String(recordId),
  });
  const response = await RequestApi.get<BookingWaiverRemovalEntry[]>(
    `/admin/booked/waiver-removals?${params.toString()}`,
  );
  return response;
};

const getOrderSourceSummary = async (filters: {
  dateFrom?: string;
  dateTo?: string;
}) => {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  const query = params.toString();

  const response = await RequestApi.get<OrderSourceSummary>(
    `/admin/order-sources${query ? `?${query}` : ''}`,
  );
  return response;
};

const getTicketAuditLog = async (filters: {
  pageNumber: number;
  pageSize: number;
  dateFrom?: string;
  dateTo?: string;
  actionType?: string;
}) => {
  const params = new URLSearchParams();
  params.set('pageNumber', String(filters.pageNumber));
  params.set('pageSize', String(filters.pageSize));
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.actionType) params.set('actionType', filters.actionType);

  const response = await RequestApi.get<TicketAuditLogResponse>(
    `/admin/booked/skating-sessions/audit-log?${params.toString()}`,
  );
  return response;
};

const getUserAccounts = async (
  pageNumber: number,
  pageSize: number,
  searchTerm: string,
) => {
  const response = await RequestApi.get<UserAccountPaginatedResponse>(
    `/admin/user-accounts?pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${searchTerm}`,
  );
  return response;
};

const getUserAccountProfile = async (userId: number) => {
  const response = await RequestApi.get<UserAccountProfileResponse>(
    `/admin/user-accounts/${userId}/profile`,
  );
  return response;
};

const updateUserAccount = async (userAccount: UpdateUserAccount) => {
  const response = await RequestApi.patch(
    `/admin/user-accounts/${userAccount.id}`,
    userAccount,
  );
  return response;
};

const updateCustomerWaiverFlag = async (
  barcodeId: string,
  customerWaiverId: number,
  isFlagged: boolean,
  flagReason: string,
) => {
  const response = await RequestApi.patch(
    `/admin/customer-waivers/${customerWaiverId}/flag`,
    { barcodeId, waiverId: customerWaiverId, isFlagged, flagReason },
  );
  return response;
};

const adminService = {
  createSeasonDuration,
  createSessionTimes,
  getCurrentSessionTimes,
  getCurrentSeasonDetails,
  getSeasons,
  toggleTicketPurchases,
  toggleSeasonPassPurchases,
  togglePartyPackagePurchases,
  toggleSkatingLessonPurchases,
  toggleHockeyLessonPurchases,
  getTimeSlotsForDate,
  updateTimeSlot,
  getCurrentPriceList,
  addPriceList,
  getCurrentWaiver,
  updateWaiver,
  getEmailTemplates,
  updateEmailTemplate,
  sendTestEmail,
  getHockeyLessons,
  updateHockeyLesson,
  getSkatingLessons,
  updateSkatingLesson,
  getBookedSessions,
  redeemBookedSkatingSession,
  getBookedSkatingLessons,
  redeemBookedSkatingLesson,
  getBookedHockeyLessons,
  redeemBookedHockeyLesson,
  getBookedSkatingPasses,
  redeemBookedSkatingPass,
  getContactUsMessages,
  markContactMessageAsRead,
  replyToContactMessage,
  getSeasonSkatingPasses,
  updateSeasonSkatingPass,
  getBookedParties,
  redeemBookedParty,
  updateBookedParty,
  getPartySessions,
  updatePartySession,
  getPartyExtras,
  updatePartyExtra,
  addBookedPartyNote,
  getPartyNotes,
  deleteBookedParty,
  getCustomerWaivers,
  getArchivedWaivers,
  searchCustomerWaivers,
  addTicketsToSession,
  removeTicketsFromSession,
  attachCustomerWaiverToRecord,
  recordPaperWaiver,
  getAdminStatistics,
  createCoupon,
  getAllCoupons,
  deleteCoupon,
  cancelSkatingSession,
  permanentlyDeleteSkatingSession,
  changeSkatingSession,
  renameSkatingSessionHolder,
  getBookedSkatingSessionHistory,
  removeAttachedWaiver,
  getWaiverRemovals,
  getTicketAuditLog,
  getOrderSourceSummary,
  getUserAccounts,
  getUserAccountProfile,
  updateUserAccount,
  updateCustomerWaiverFlag,
};

export default adminService;
